import CloseIcon from '@mui/icons-material/Close'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link as MuiLink,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { format } from 'date-fns'
import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { appointmentsApi } from '../../osa-scheduling/api/appointments.api'
import type { Appointment } from '../../osa-scheduling/types'
import { exportUnscheduledProjects } from '../utils/export.utils'

interface UnscheduledProjectsDialogProps {
  open: boolean
  onClose: () => void
  unscheduledCount: number
}

const UnscheduledProjectsDialog: React.FC<UnscheduledProjectsDialogProps> = ({
  open,
  onClose,
  unscheduledCount,
}) => {
  const [appointments, setAppointments] = useState<Appointment[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  useEffect(() => {
    if (!open) return

    const fetchUnscheduledAppointments = async () => {
      setLoading(true)
      setError(null)
      try {
        // Fetch unscheduled appointments using the count as pageSize
        // This gets the oldest appointments that are unscheduled (>24hr old)
        const response = await appointmentsApi.getAppointments({
          status: 'new',
          sortField: 'createdAt',
          sortOrder: 'asc',
          pageSize: unscheduledCount, // Use unscheduled count as page size
          env: 'live',
        })

        setAppointments(response.data)
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load unscheduled appointments'
        setError(errorMessage)
        console.error('Error fetching unscheduled appointments:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchUnscheduledAppointments()
  }, [open, unscheduledCount])

  const formatDateTime = (dateString: string): string => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm')
    } catch (error) {
      console.error('Error formatting date:', error)
      return '-'
    }
  }

  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleExportClose = () => {
    setAnchorEl(null)
  }

  const handleExport = (format: 'csv' | 'xlsx') => {
    exportUnscheduledProjects(appointments, format)
    handleExportClose()
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={{
        '& .MuiDialog-paper': {
          width: '90vw',
          maxWidth: '1100px',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Unscheduled Projects (&gt;24hr)</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {appointments.length > 0 && !loading && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportClick}
                  sx={{ mr: 1 }}
                >
                  Export
                </Button>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleExportClose}>
                  <MenuItem onClick={() => handleExport('xlsx')}>Export as Excel (.xlsx)</MenuItem>
                  <MenuItem onClick={() => handleExport('csv')}>Export as CSV (.csv)</MenuItem>
                </Menu>
              </>
            )}
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Typography color="error" sx={{ py: 2 }}>
            {error}
          </Typography>
        )}

        {!loading && !error && appointments.length === 0 && (
          <Typography sx={{ py: 2, textAlign: 'center', color: 'text.secondary' }}>
            No unscheduled projects found
          </Typography>
        )}

        {!loading && !error && appointments.length > 0 && (
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Scout Assignments</TableCell>
                    <TableCell>Scout Notification Sent</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {appointments.map(appointment => (
                    <TableRow
                      key={appointment.uuid}
                      component={Link}
                      to={`/osa-scheduling/show/${appointment.uuid}`}
                      sx={{
                        cursor: 'pointer',
                        textDecoration: 'none',
                        color: 'inherit',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {appointment.customerName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const address = appointment.customerAddress
                          if (!address || address === '-') {
                            return <Typography variant="body2">-</Typography>
                          }
                          const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
                          return (
                            <MuiLink
                              href={googleMapsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={e => e.stopPropagation()}
                              sx={{
                                color: 'primary.main',
                                textDecoration: 'none',
                                '&:hover': {
                                  textDecoration: 'underline',
                                },
                              }}
                            >
                              {address}
                            </MuiLink>
                          )
                        })()}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {appointment.scoutCount ? `✅ ${appointment.scoutCount}` : '❌ 0'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {appointment.scoutNotificationSent
                            ? formatDateTime(appointment.scoutNotificationSent)
                            : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDateTime(appointment.createdAt)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default UnscheduledProjectsDialog
